import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Container,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AccountCircle,
  Lock as LockIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { formatRUT, validateRUT, validateEmail, validatePassword } from '../utils/validators';
import { UserCreate } from '../types';
import PasswordManager from '../utils/passwordManager';
import PrimaryButton from '../components/PrimaryButton';

interface RegisterFormData extends UserCreate {
  confirmPassword: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<RegisterFormData>();

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const passwordValidation = password ? validatePassword(password) : { valid: false, errors: [] };

  const handleRUTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatRUT(e.target.value);
    setValue('rut', formatted);
  };

  const handleNext = async () => {
    const fieldsToValidate = activeStep === 0 
      ? ['rut', 'email', 'full_name'] 
      : ['password', 'confirmPassword'];
    
    const isValid = await trigger(fieldsToValidate as any);
    if (isValid) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const onSubmit = async (data: RegisterFormData) => {
    setError('');
    setLoading(true);
    
    try {
      // Excluir confirmPassword del objeto a enviar
      const { confirmPassword, ...userData } = data;
      await authService.register(userData);
      
      // Auto login después del registro
      await authService.login({
        username: data.rut,
        password: data.password,
      });
      
      // Guardar la contraseña para uso en información de emergencia (10 minutos)
      PasswordManager.setTemporaryPassword(data.password, 600000);
      
      navigate('/dashboard');
    } catch (err: any) {
      // Manejar errores de validación 422 de FastAPI
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const validationErrors = err.response.data.detail;
        if (Array.isArray(validationErrors)) {
          // Formatear errores de validación de Pydantic
          const errorMessages = validationErrors.map((error: any) => {
            const field = error.loc?.join(' -> ') || 'Campo';
            return `${field}: ${error.msg}`;
          }).join(', ');
          setError(errorMessages);
        } else {
          setError(validationErrors);
        }
      } else {
        // Manejar otros tipos de errores
        setError(err.response?.data?.detail || t('auth.register.errors.createError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [t('auth.register.steps.personal'), t('auth.register.steps.password')];

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <PersonIcon sx={{ color: 'white', fontSize: 30 }} />
          </Box>
          
          <Typography component="h1" variant="h5" fontWeight="bold" mb={3}>
            {t('auth.register.title')}
          </Typography>
          
          <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
            {activeStep === 0 && (
              <>
                <TextField
                  fullWidth
                  label={t('auth.register.rut')}
                  placeholder="12.345.678-9"
                  margin="normal"
                  {...register('rut', {
                    required: t('auth.register.errors.rutRequired'),
                    validate: (value) => validateRUT(value) || t('auth.register.errors.rutInvalid'),
                  })}
                  onChange={handleRUTChange}
                  error={!!errors.rut}
                  helperText={errors.rut?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label={t('auth.register.email')}
                  type="email"
                  margin="normal"
                  {...register('email', {
                    required: t('auth.register.errors.emailRequired'),
                    validate: (value) => validateEmail(value) || t('auth.register.errors.emailInvalid'),
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label={t('auth.register.fullName')}
                  margin="normal"
                  {...register('full_name')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
            
            {activeStep === 1 && (
              <>
                <TextField
                  fullWidth
                  label={t('auth.register.password')}
                  type={showPassword ? 'text' : 'password'}
                  margin="normal"
                  {...register('password', {
                    required: t('auth.register.errors.passwordRequired'),
                    validate: (value) => validatePassword(value).valid || t('auth.register.errors.passwordInvalid'),
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label={t('auth.register.confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  margin="normal"
                  {...register('confirmPassword', {
                    required: t('auth.register.errors.confirmRequired'),
                    validate: (value) => value === password || t('auth.register.errors.passwordMismatch'),
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('auth.register.passwordRequirements.title')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        {password?.length >= 8 ? 
                          <CheckIcon color="success" fontSize="small" /> : 
                          <CloseIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('auth.register.passwordRequirements.minLength')} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        {/[A-Z]/.test(password || '') ? 
                          <CheckIcon color="success" fontSize="small" /> : 
                          <CloseIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('auth.register.passwordRequirements.uppercase')} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        {/[a-z]/.test(password || '') ? 
                          <CheckIcon color="success" fontSize="small" /> : 
                          <CloseIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('auth.register.passwordRequirements.lowercase')} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        {/[0-9]/.test(password || '') ? 
                          <CheckIcon color="success" fontSize="small" /> : 
                          <CloseIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('auth.register.passwordRequirements.number')} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        {password && confirmPassword && password === confirmPassword ? 
                          <CheckIcon color="success" fontSize="small" /> : 
                          <CloseIcon color="error" fontSize="small" />
                        }
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('auth.register.passwordRequirements.match')} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                {t('auth.register.back')}
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <PrimaryButton
                  type="submit"
                  size="large"
                  disabled={loading || !passwordValidation.valid || password !== confirmPassword}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : t('auth.register.button')}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={handleNext}
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                  }}
                >
                  {t('auth.register.next')}
                </PrimaryButton>
              )}
            </Box>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('auth.register.hasAccount')}{' '}
                <Link to="/login" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  {t('auth.register.loginHere')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register; 